import React, { useEffect, useRef, useState } from "react";
import {
    Dialog, AppBar, Toolbar, IconButton, Typography, Slide, Box, List, ListItem, ListItemText,
    ListItemSecondaryAction, Chip, Button, CircularProgress, Alert, TextField, MenuItem, Grid,
    Stack, Switch, FormControlLabel, Divider, DialogTitle, DialogContent, DialogActions, Paper, Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import StorefrontIcon from "@mui/icons-material/Storefront";
import livechatApi from "./livechatApi";
import { PASTEL, getBrandColor } from "./livechatTheme";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const TIMEZONES = ["Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura"];

const emptyForm = {
    brand: "",
    waNumber: "",
    chatEnabled: true,
    chatHoursStart: "08:00",
    chatHoursEnd: "20:00",
    chatHoursTimezone: "Asia/Jakarta",
    chatGreeting: "",
    chatWelcome: "",
    chatOnlineText: "",
    chatOfflineText: "",
    appProfileId: "",
    brandIcon: null,
};

const toFormState = (settings) => ({
    brand: settings.brand || "",
    waNumber: settings.waNumber || "",
    chatEnabled: settings.chatEnabled !== false,
    chatHoursStart: settings.chatHours?.start || "08:00",
    chatHoursEnd: settings.chatHours?.end || "20:00",
    chatHoursTimezone: settings.chatHours?.timezone || "Asia/Jakarta",
    chatGreeting: settings.chatGreeting || "",
    chatWelcome: settings.chatWelcome || "",
    chatOnlineText: settings.chatOnlineText || "",
    chatOfflineText: settings.chatOfflineText || "",
    appProfileId: settings.appProfileId || "",
    brandIcon: settings.brandIcon || null,
});

const toRequestBody = (form) => ({
    brand: form.brand.trim().toLowerCase(),
    waNumber: form.waNumber.trim(),
    chatEnabled: !!form.chatEnabled,
    chatHours: {
        start: form.chatHoursStart,
        end: form.chatHoursEnd,
        timezone: form.chatHoursTimezone,
    },
    chatGreeting: form.chatGreeting.trim(),
    chatWelcome: form.chatWelcome.trim(),
    chatOnlineText: form.chatOnlineText.trim(),
    chatOfflineText: form.chatOfflineText.trim(),
    appProfileId: form.appProfileId || null,
    brandIcon: form.brandIcon || null,
});

const REQUIRED_FIELDS = [
    ["brand", "Brand"], ["waNumber", "Nomor WhatsApp"], ["chatHoursStart", "Jam mulai"], ["chatHoursEnd", "Jam selesai"],
    ["chatGreeting", "Sapaan pembuka"], ["chatWelcome", "Pesan selamat datang"],
    ["chatOnlineText", "Teks status online"], ["chatOfflineText", "Teks status offline"],
];

/**
 * Halaman "Pengaturan Brand Live Chat", dibangun sebagai Dialog fullscreen (bukan route
 * terpisah) sesuai arahan — CRUD ke LiveChatBrandSettings lewat endpoint yang sudah ada di
 * lotus-be-express (lihat swagger, tag "Live Chat - Agent", path /livechat/agent/brand-settings).
 */
export default function LiveChatSettingsDialog({ open, onClose, onChanged }) {
    const [view, setView] = useState("list");
    const [items, setItems] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [listError, setListError] = useState("");

    const [editingBrand, setEditingBrand] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [appProfiles, setAppProfiles] = useState([]);
    const [uploadingIcon, setUploadingIcon] = useState(false);
    const iconInputRef = useRef(null);

    const loadList = async () => {
        setLoadingList(true);
        setListError("");
        try {
            const { data } = await livechatApi.listBrandSettings();
            setItems(data.data?.settings || []);
        } catch (e) {
            setListError(e?.response?.data?.message || "Gagal memuat daftar pengaturan brand");
        } finally {
            setLoadingList(false);
        }
    };

    const loadAppProfiles = async () => {
        try {
            const { data } = await livechatApi.listAppProfiles();
            setAppProfiles(data.data || []);
        } catch (e) {
            setAppProfiles([]);
        }
    };

    useEffect(() => {
        if (open) {
            setView("list");
            loadList();
            loadAppProfiles();
        }
    }, [open]);

    const findAppProfile = (id) => appProfiles.find((p) => p._id === id);

    const handlePickIcon = () => iconInputRef.current?.click();

    const handleIconSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        setUploadingIcon(true);
        setFormError("");
        try {
            const { data } = await livechatApi.uploadPhoto(file);
            setForm((prev) => ({ ...prev, brandIcon: data.data?.url || null }));
        } catch (err) {
            setFormError(err?.response?.data?.message || "Gagal upload ikon brand");
        } finally {
            setUploadingIcon(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingBrand(null);
        setForm(emptyForm);
        setFormError("");
        setView("form");
    };

    const handleOpenEdit = (settings) => {
        setEditingBrand(settings.brand);
        setForm(toFormState(settings));
        setFormError("");
        setView("form");
    };

    const handleBackToList = () => {
        setView("list");
        loadList();
    };

    const handleSave = async () => {
        setFormError("");
        const missing = REQUIRED_FIELDS.filter(([key]) => !String(form[key] || "").trim());
        if (missing.length > 0) {
            setFormError(`Field wajib belum diisi: ${missing.map(([, label]) => label).join(", ")}`);
            return;
        }

        setSaving(true);
        try {
            const body = toRequestBody(form);
            if (editingBrand) {
                const { brand: _ignored, ...updateBody } = body;
                await livechatApi.updateBrandSettings(editingBrand, updateBody);
            } else {
                await livechatApi.createBrandSettings(body);
            }
            onChanged && onChanged();
            setView("list");
            loadList();
        } catch (e) {
            setFormError(e?.response?.data?.message || "Gagal menyimpan pengaturan brand");
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await livechatApi.deleteBrandSettings(deleteTarget);
            setDeleteTarget(null);
            onChanged && onChanged();
            loadList();
        } catch (e) {
            setListError(e?.response?.data?.message || "Gagal menghapus pengaturan brand");
            setDeleteTarget(null);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={open} maxWidth="md" TransitionComponent={Transition}>
            <AppBar position="relative" color="inherit" elevation={0} sx={{ borderBottom: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface }}>
                <Toolbar sx={{ gap: 1 }}>
                    <IconButton
                        edge="start"
                        onClick={view === "form" ? handleBackToList : onClose}
                        aria-label={view === "form" ? "Kembali" : "Tutup"}
                    >
                        {view === "form" ? <ArrowBackIcon /> : <CloseIcon />}
                    </IconButton>
                    <SettingsIcon sx={{ color: PASTEL.primary }} fontSize="small" />
                    <Typography variant="h6" sx={{ flex: 1, fontSize: 17, fontWeight: 700 }}>
                        {view === "list" ? "Pengaturan Brand Live Chat" : editingBrand ? `Edit Brand: ${editingBrand}` : "Tambah Brand Baru"}
                    </Typography>
                    {view === "list" ? (
                        <Button startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ textTransform: "none", fontWeight: 600 }}>
                            Tambah Brand
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon fontSize="small" />}
                            onClick={handleSave}
                            disabled={saving}
                            sx={{ textTransform: "none", backgroundColor: PASTEL.primary, "&:hover": { backgroundColor: PASTEL.primaryDark } }}
                        >
                            {saving ? "Menyimpan..." : "Simpan"}
                        </Button>
                    )}
                </Toolbar>
            </AppBar>

            <Box sx={{ flex: 1, overflowY: "auto", backgroundColor: PASTEL.background }}>
                {view === "list" ? (
                    <Box sx={{ maxWidth: 720, mx: "auto", p: { xs: 2, sm: 3 } }}>
                        {listError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{listError}</Alert>}

                        {loadingList ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                <CircularProgress sx={{ color: PASTEL.primary }} />
                            </Box>
                        ) : items.length === 0 ? (
                            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                                Belum ada pengaturan brand. Klik "Tambah Brand" untuk membuat yang pertama.
                            </Alert>
                        ) : (
                            <Paper elevation={0} sx={{ border: `1px solid ${PASTEL.border}`, borderRadius: 3, backgroundColor: PASTEL.surface }}>
                                <List disablePadding>
                                    {items.map((it, idx) => (
                                        <React.Fragment key={it.brand}>
                                            {idx > 0 && <Divider />}
                                            <ListItem sx={{ py: 1.5, pr: 12 }}>
                                                {it.brandIcon ? (
                                                    <Avatar src={it.brandIcon} sx={{ width: 28, height: 28, mr: 1.5, flexShrink: 0 }} />
                                                ) : (
                                                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: getBrandColor(it.brand), mr: 1.5, flexShrink: 0 }} />
                                                )}
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Typography variant="subtitle2" fontWeight={700}>{it.brand}</Typography>
                                                            <Chip
                                                                size="small"
                                                                label={it.chatEnabled !== false ? "Aktif" : "Nonaktif"}
                                                                color={it.chatEnabled !== false ? "success" : "default"}
                                                                sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                                            />
                                                        </Stack>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" color="text.secondary">
                                                            {it.waNumber || "-"} · {it.chatHours?.start || "?"}–{it.chatHours?.end || "?"} ({it.chatHours?.timezone || "-"})
                                                            {it.appProfileId && findAppProfile(it.appProfileId) && (
                                                                <> · App Profile: {findAppProfile(it.appProfileId).label}</>
                                                            )}
                                                        </Typography>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton size="small" onClick={() => handleOpenEdit(it)} sx={{ mr: 0.5 }} aria-label="Edit">
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => setDeleteTarget(it.brand)} color="error" aria-label="Hapus">
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ maxWidth: 720, mx: "auto", p: { xs: 2, sm: 3 } }}>
                        {formError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{formError}</Alert>}

                        <Paper elevation={0} sx={{ border: `1px solid ${PASTEL.border}`, borderRadius: 3, backgroundColor: PASTEL.surface, p: { xs: 2, sm: 3 } }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth size="small" label="Brand (slug)" placeholder="prestisa"
                                        value={form.brand} disabled={!!editingBrand}
                                        onChange={(e) => setForm({ ...form, brand: e.target.value })}
                                        helperText={editingBrand ? "Slug tidak bisa diubah setelah dibuat" : "Huruf kecil, mis. prestisa / parselia"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth size="small" label="Nomor WhatsApp" placeholder="628123456789"
                                        value={form.waNumber}
                                        onChange={(e) => setForm({ ...form, waNumber: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        {form.brandIcon ? (
                                            <Avatar src={form.brandIcon} sx={{ width: 44, height: 44 }} />
                                        ) : (
                                            <Avatar sx={{ width: 44, height: 44, backgroundColor: PASTEL.primarySoft, color: PASTEL.primary }}>
                                                <StorefrontIcon fontSize="small" />
                                            </Avatar>
                                        )}
                                        <Stack spacing={0.5}>
                                            <Button
                                                size="small" variant="outlined" startIcon={<AddPhotoAlternateIcon fontSize="small" />}
                                                onClick={handlePickIcon} disabled={uploadingIcon}
                                                sx={{ textTransform: "none" }}
                                            >
                                                {uploadingIcon ? "Mengupload..." : "Upload Ikon Brand"}
                                            </Button>
                                            {form.brandIcon && (
                                                <Button
                                                    size="small" color="error" onClick={() => setForm({ ...form, brandIcon: null })}
                                                    sx={{ textTransform: "none", justifyContent: "flex-start" }}
                                                >
                                                    Hapus ikon
                                                </Button>
                                            )}
                                        </Stack>
                                        <input
                                            ref={iconInputRef} type="file" accept="image/*" hidden
                                            onChange={handleIconSelected}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select fullWidth size="small" label="App Profile terhubung (opsional)"
                                        value={form.appProfileId}
                                        onChange={(e) => setForm({ ...form, appProfileId: e.target.value })}
                                        helperText="Untuk mengaitkan brand ini ke salah satu App Profile Lotus"
                                    >
                                        <MenuItem value="">Tidak ada</MenuItem>
                                        {appProfiles.map((p) => (
                                            <MenuItem key={p._id} value={p._id}>
                                                {p.label} — {p.waNumber}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={form.chatEnabled} onChange={(e) => setForm({ ...form, chatEnabled: e.target.checked })} />}
                                        label="Widget live chat aktif untuk brand ini"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth size="small" type="time" label="Jam mulai" InputLabelProps={{ shrink: true }}
                                        value={form.chatHoursStart}
                                        onChange={(e) => setForm({ ...form, chatHoursStart: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth size="small" type="time" label="Jam selesai" InputLabelProps={{ shrink: true }}
                                        value={form.chatHoursEnd}
                                        onChange={(e) => setForm({ ...form, chatHoursEnd: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        select fullWidth size="small" label="Timezone"
                                        value={form.chatHoursTimezone}
                                        onChange={(e) => setForm({ ...form, chatHoursTimezone: e.target.value })}
                                    >
                                        {TIMEZONES.map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth size="small" multiline minRows={2} label="Sapaan pembuka (chatGreeting)"
                                        placeholder="Halo {nama}, ada yang bisa kami bantu?"
                                        value={form.chatGreeting}
                                        onChange={(e) => setForm({ ...form, chatGreeting: e.target.value })}
                                        helperText="Placeholder {nama} dan {brand} otomatis diganti"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth size="small" multiline minRows={2} label="Pesan selamat datang (chatWelcome)"
                                        placeholder="Terima kasih sudah menghubungi {brand}, {nama}!"
                                        value={form.chatWelcome}
                                        onChange={(e) => setForm({ ...form, chatWelcome: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth size="small" label="Teks status online (chatOnlineText)" placeholder="Online"
                                        value={form.chatOnlineText}
                                        onChange={(e) => setForm({ ...form, chatOnlineText: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth size="small" label="Teks status offline (chatOfflineText)" placeholder="Di luar jam operasional"
                                        value={form.chatOfflineText}
                                        onChange={(e) => setForm({ ...form, chatOfflineText: e.target.value })}
                                        helperText="Dipakai saat di luar jam operasional & saat percakapan ditutup"
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>
                )}
            </Box>

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Hapus pengaturan brand?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Pengaturan untuk brand <strong>{deleteTarget}</strong> akan dihapus permanen. Widget live chat brand ini akan jatuh ke pengaturan default sampai dibuat ulang.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none" }}>Batal</Button>
                    <Button onClick={handleConfirmDelete} disabled={deleting} color="error" variant="contained" sx={{ textTransform: "none" }}>
                        {deleting ? "Menghapus..." : "Hapus"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
